import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Subject, Observable, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private sockets: { [key: string]: WebSocketSubject<any> } = {};
  private messageSubjects: { [key: string]: Subject<any> } = {};
  private connectionStatus: { [key: string]: Subject<boolean> } = {};

  constructor(private keycloakService: KeycloakService) { }

  private async getToken(): Promise<string> {
    try {
      return await this.keycloakService.getToken();
    } catch {
      return '';
    }
  }

  private normalizeUrl(url: string): string {
    return url.replace(/([^:]\/)\/+/g, '$1');
  }

  async connect(key: string, url: string): Promise<void> {
    if (this.sockets[key] && !this.sockets[key].closed) return;

    const token = await this.getToken();
    const finalUrl = this.normalizeUrl(`${url}?token=${encodeURIComponent(token)}`);

    const subject = webSocket({
      url: finalUrl,
      openObserver: { next: () => this.connectionStatus[key]?.next(true) },
      closeObserver: { next: () => this.connectionStatus[key]?.next(false) },
      serializer: msg => JSON.stringify(msg),
      deserializer: ({ data }) => {
        try {
          return JSON.parse(data);
        } catch {
          return data;
        }
      }
    });

    this.sockets[key] = subject;
    this.messageSubjects[key] ??= new Subject<any>();
    this.connectionStatus[key] ??= new Subject<boolean>();

    subject.pipe(
      catchError(err => {
        this.connectionStatus[key]?.next(false);
        return EMPTY;
      })
    ).subscribe(this.messageSubjects[key]);
  }

  disconnect(key: string) {
    this.sockets[key]?.complete();
    delete this.sockets[key];
    this.messageSubjects[key]?.complete();
    delete this.messageSubjects[key];
    this.connectionStatus[key]?.complete();
    delete this.connectionStatus[key];
  }

  onMessage(key: string): Observable<any> {
    this.messageSubjects[key] ??= new Subject<any>();
    return this.messageSubjects[key].asObservable();
  }

  getConnectionStatus(key: string): Observable<boolean> {
    this.connectionStatus[key] ??= new Subject<boolean>();
    return this.connectionStatus[key].asObservable();
  }

  sendMessage(key: string, msg: any) {
    this.sockets[key]?.next(msg);
  }
}